'use client';

import { File, Loader2, X } from 'lucide-react';

import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { convertArrayBufferToFile } from '@/utils/common';
import { ATTACHMENT_MAX_FILE_SIZE } from '@/utils/constants';

interface IAttachmentFieldProps {
	fieldId?: string;
	acceptedFileTypes?: string;
	editMode?: boolean;
	onUploadFile?: (file: File) => void;
	onRemoveFile?: () => void;
	onFileError?: (error: string) => void;
	isUploading?: boolean;
	error?: boolean;
	disabled?: boolean;
	attachedFileName?: string;
}

function AttachmentField({
	acceptedFileTypes,
	editMode,
	onUploadFile,
	onRemoveFile,
	isUploading,
	disabled,
	onFileError,
	attachedFileName,
}: IAttachmentFieldProps) {
	const [file, setFile] = useState<File>();
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		if (isUploading || !event.target.files?.length) return;

		if (event.target.files[0].size > ATTACHMENT_MAX_FILE_SIZE) {
			onFileError?.('File size must be less than 10 MB');
			return;
		}

		try {
			const newFiles = Array.from(event.target.files || []);

			const fileToUpload = await convertArrayBufferToFile(newFiles[0]);

			setFile(fileToUpload);
			onUploadFile?.(fileToUpload);
		} catch (error) {
			onFileError?.('Something went wrong. Please try again');
		}
	};

	const handleRemoveFile = () => {
		if (isUploading) return;

		if (fileInputRef.current?.value) {
			fileInputRef.current.value = '';
		}

		setFile(undefined);
		onRemoveFile?.();
	};

	const handleButtonClick = () => {
		if (isUploading) return;

		fileInputRef.current?.click();
	};

	return (
		<div className="space-y-4">
			<div className="flex items-center space-x-2">
				<Button
					type="button"
					variant="outline"
					className="h-[44px] gap-2 border-border text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-100"
					onClick={handleButtonClick}
					disabled={editMode || disabled}
				>
					<svg
						width="16px"
						height="16px"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 8L12 3M12 3L7 8M12 3V15"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
					Add file <span className="text-xs text-muted-foreground">(max 10 MB)</span>
				</Button>
				<Input
					type="file"
					ref={fileInputRef}
					className="hidden"
					onChange={handleFileChange}
					multiple={false}
					accept={acceptedFileTypes}
				/>
			</div>
			{!!file || attachedFileName ? (
				<div className="flex w-fit max-w-64 items-center justify-between gap-2 overflow-hidden rounded-md bg-muted p-2">
					<div className="flex items-center gap-2 overflow-hidden">
						<File className="inline-block h-4 w-4 flex-shrink-0" />
						<span className="inline-block truncate text-sm font-medium">
							{file?.name || attachedFileName}
						</span>
						{/* <span className="text-xs text-muted-foreground">
							({(file.size / 1024).toFixed(2)} KB)
						</span> */}
					</div>
					{isUploading ? (
						<Loader2 className="h-4 w-4 animate-spin" />
					) : (
						<Button
							type="button"
							variant="ghost"
							className="h-full p-0"
							onClick={handleRemoveFile}
						>
							<X className="h-4 w-4" />
						</Button>
					)}
				</div>
			) : null}
		</div>
	);
}

export default AttachmentField;
